import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useOffline } from './OfflineContext';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const { isOnline, saveMenuCache, loadMenuCache, submitOrder, setOutletId } = useOffline();
  const [outlets, setOutlets] = useState([]);
  const [currentOutlet, setCurrentOutlet] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch outlets when user logs in
  const fetchOutlets = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/outlets`, { withCredentials: true });
      setOutlets(response.data);
      // Auto-select first outlet or user's assigned outlet
      if (response.data.length > 0) {
        const userOutlet = response.data.find(o => o.id === user.outlet_id) || response.data[0];
        setCurrentOutlet(userOutlet);
      }
    } catch (e) {
      console.error('Error fetching outlets:', e);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchOutlets();
    }
  }, [user, fetchOutlets]);

  // Fetch menu data when outlet changes (with offline fallback)
  const fetchMenuData = useCallback(async () => {
    if (!currentOutlet) return;
    setLoading(true);
    setOutletId(currentOutlet.id);
    // Clear stale data immediately so the UI doesn't flash the previous outlet's numbers
    setCategories([]);
    setMenuItems([]);
    setModifierGroups([]);
    setTables([]);
    setOrders([]);
    try {
      const [catRes, itemRes, modRes, tableRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/menu-items?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/modifier-groups?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tables?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
      ]);
      setCategories(catRes.data);
      setMenuItems(itemRes.data);
      setModifierGroups(modRes.data);
      setTables(tableRes.data);
      // Cache for offline use
      saveMenuCache(currentOutlet.id, {
        categories: catRes.data,
        items: itemRes.data,
        modifiers: modRes.data,
        tables: tableRes.data,
      });
    } catch (e) {
      console.error('Error fetching menu data (likely offline):', e);
      // Try offline cache
      const cached = await loadMenuCache(currentOutlet.id);
      if (cached) {
        setCategories(cached.categories || []);
        setMenuItems(cached.items || []);
        setModifierGroups(cached.modifiers || []);
        setTables(cached.tables || []);
      }
    } finally {
      setLoading(false);
    }
  }, [currentOutlet, saveMenuCache, loadMenuCache, setOutletId]);

  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // Fetch orders
  const fetchOrders = useCallback(async (status = null) => {
    if (!currentOutlet) return;
    try {
      let url = `${API_URL}/api/orders?outlet_id=${currentOutlet.id}`;
      if (status) url += `&status=${status}`;
      const response = await axios.get(url, { withCredentials: true });
      setOrders(response.data);
      return response.data;
    } catch (e) {
      console.error('Error fetching orders:', e);
      return [];
    }
  }, [currentOutlet]);

  // Create order — routes through offline-aware queue
  const createOrder = async (orderData) => {
    try {
      const payload = { ...orderData, outlet_id: currentOutlet.id };
      const result = await submitOrder(payload);
      if (result.online) {
        await fetchOrders();
        return result.order;
      }
      // Queued offline — return a synthetic placeholder so UI can proceed
      return {
        id: `local-${result.queued.local_id}`,
        order_number: `OFFLINE-${result.queued.local_id}`,
        token_number: result.queued.local_id,
        offline: true,
        ...payload,
      };
    } catch (e) {
      console.error('Error creating order:', e);
      throw e;
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status?status=${status}`, {}, { withCredentials: true });
      await fetchOrders();
    } catch (e) {
      console.error('Error updating order:', e);
      throw e;
    }
  };

  // Process payment
  const processPayment = async (paymentData) => {
    try {
      const response = await axios.post(`${API_URL}/api/payments`, paymentData, { withCredentials: true });
      await fetchOrders();
      return response.data;
    } catch (e) {
      console.error('Error processing payment:', e);
      throw e;
    }
  };

  // Get dashboard stats
  const getDashboardStats = useCallback(async () => {
    if (!currentOutlet) return null;
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats?outlet_id=${currentOutlet.id}`, { withCredentials: true });
      return response.data;
    } catch (e) {
      console.error('Error fetching dashboard stats:', e);
      return null;
    }
  }, [currentOutlet]);

  // Update table status
  const updateTableStatus = async (tableId, status, orderId = null) => {
    try {
      let url = `${API_URL}/api/tables/${tableId}/status?status=${status}`;
      if (orderId) url += `&order_id=${orderId}`;
      await axios.patch(url, {}, { withCredentials: true });
      await fetchMenuData();
    } catch (e) {
      console.error('Error updating table:', e);
      throw e;
    }
  };

  const value = {
    outlets,
    currentOutlet,
    setCurrentOutlet,
    categories,
    menuItems,
    modifierGroups,
    tables,
    orders,
    loading,
    fetchOutlets,
    fetchMenuData,
    fetchOrders,
    createOrder,
    updateOrderStatus,
    processPayment,
    getDashboardStats,
    updateTableStatus,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
